/**
 * LeaderboardProjector — Modo LIVE fullscreen para projectar em TV/monitor
 * no final de um passeio Nav Challenge.
 *
 * Design Rugged aligned com mock-admin-dashboard-rugged.html:
 *   - Header terracota gradient com LIVE dot pulsante
 *   - Rows dark warm com bordas sand
 *   - Podium: gold/silver/bronze
 *   - Auto-refresh 10s
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEDALS = ['🥇', '🥈', '🥉'];
const C = {
  bg: '#0F0D0B',
  bg2: '#1A1613',
  brand: '#E87A2A',
  brandHover: '#F0902F',
  sand: '#C4A275',
  ink: '#F2EBDD',
  inkDim: 'rgba(242, 235, 221, 0.55)',
  danger: '#EF4444',
  gold: '#FACC15',
  silver: '#C0C0C0',
  bronze: '#B87333',
  cardBrd: 'rgba(196, 162, 117, 0.20)',
};

export default function LeaderboardProjector() {
  const { challengeId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const fetchIt = () => {
      axios
        .get(`${API}/nav-challenge/${challengeId}/leaderboard`, { withCredentials: true })
        .then((res) => { setData(res.data); setError(null); setNow(Date.now()); })
        .catch((e) => setError(e.response?.data?.detail || 'Erro ao carregar leaderboard'));
    };
    fetchIt();
    const t = setInterval(fetchIt, 10000);
    return () => clearInterval(t);
  }, [challengeId]);

  const rows = data?.leaderboard || [];
  const ended = !!data?.ended_at;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bg2} 100%)`,
        color: C.ink,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
      data-testid="leaderboard-projector"
    >
      {/* Header terracota gradient com LIVE indicator */}
      <div
        style={{
          background: `linear-gradient(90deg, ${C.brand} 0%, ${C.brandHover} 100%)`,
          color: C.bg,
          padding: '24px 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(232,122,42,0.4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {!ended && (
            <span
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: C.danger,
                boxShadow: `0 0 16px ${C.danger}`,
                animation: 'live-pulse 1.2s infinite',
              }}
              aria-label="Ao vivo"
            />
          )}
          <div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 42,
              fontWeight: 900,
              letterSpacing: 4,
              lineHeight: 1,
            }}>
              🏁 {data?.challenge_name || data?.name || 'NAVIGATION CHALLENGE'}
            </div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: 3,
              marginTop: 6,
              opacity: 0.9,
            }}>
              {ended ? '🏆 RESULTADO FINAL' : '⚡ AO VIVO'} · ACTUALIZA A CADA 10s
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: 11,
            letterSpacing: 2,
            opacity: 0.8,
          }}>
            TECTUR 4×4 · RALLY PAPER
          </div>
          <div style={{
            fontSize: 10,
            marginTop: 4,
            opacity: 0.7,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {new Date(now).toLocaleTimeString('pt-PT')}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes live-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.3); }
        }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 32px' }}>
        {error && (
          <p style={{ color: C.danger, fontSize: 20, textAlign: 'center', marginTop: 40 }} data-testid="projector-error">
            {error}
          </p>
        )}

        {rows.length === 0 && !error && (
          <div
            style={{
              textAlign: 'center',
              padding: '80px 20px',
              color: C.inkDim,
              fontSize: 24,
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: 3,
            }}
            data-testid="projector-empty"
          >
            AINDA SEM PARTICIPANTES...
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((r, i) => {
            const isPodium = i < 3;
            const podiumColor = i === 0 ? C.gold : i === 1 ? C.silver : C.bronze;
            const rowBg = i === 0
              ? `linear-gradient(90deg, rgba(250,204,21,0.15), rgba(250,204,21,0.05))`
              : i === 1
                ? `linear-gradient(90deg, rgba(192,192,192,0.10), transparent)`
                : i === 2
                  ? `linear-gradient(90deg, rgba(184,115,51,0.15), transparent)`
                  : 'rgba(26,22,19,0.6)';

            return (
              <div
                key={r.nickname || i}
                data-testid={`projector-row-${i + 1}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 24,
                  padding: '18px 24px',
                  borderRadius: 12,
                  background: rowBg,
                  border: `${isPodium ? '2px' : '1px'} solid ${isPodium ? podiumColor : C.cardBrd}`,
                }}
              >
                <span style={{
                  fontSize: isPodium ? 48 : 32,
                  width: 68,
                  textAlign: 'center',
                  fontWeight: 900,
                  color: isPodium ? podiumColor : C.sand,
                }}>
                  {MEDALS[i] || `${i + 1}`}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontWeight: 900,
                    fontSize: isPodium ? 32 : 22,
                    color: isPodium ? C.ink : C.sand,
                    fontFamily: "'Bebas Neue', sans-serif",
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                  }}
                >
                  {r.nickname}
                </span>
                <span style={{
                  color: C.inkDim,
                  fontSize: 14,
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: 1,
                }}>
                  {r.correct_count}/{r.answered_count} certas
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 900,
                    fontSize: isPodium ? 36 : 26,
                    color: isPodium ? podiumColor : C.brand,
                    minWidth: 140,
                    textAlign: 'right',
                    letterSpacing: 1,
                  }}
                >
                  {r.points} pts
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '20px',
        color: 'rgba(196, 162, 117, 0.4)',
        fontSize: 11,
        letterSpacing: 3,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        ▲ TECTUR 4×4 NAVIGATION · {ended ? 'DESAFIO TERMINADO' : 'DESAFIO ACTIVO'}
      </div>
    </div>
  );
}
